import React, { useState, useEffect } from 'react';
import { FiDownload, FiPrinter, FiSend, FiCheck, FiX, FiFileText } from 'react-icons/fi';
import { supabase } from '../../lib/supabase';
import SafeIcon from '../../common/SafeIcon';
import {
  generateForm130U,
  generateBillOfSale,
  generateBuyersGuide,
  generateOdometerDisclosure,
  generateInsuranceVerification,
  generateDriverLicenseForm,
  downloadForm,
  printForm
} from '../../lib/dealershipForms';

const DealJacket = ({ dealId, dealData, onClose }) => {
  const [activeTab, setActiveTab] = useState('overview');
  
  // Debug: Log the deal data being passed
  console.log('DealJacket received dealData:', dealData);
  const [documents, setDocuments] = useState([
    { 
      id: 'form130u', 
      name: 'Form 130-U (Title Application)', 
      status: 'pending',
      generator: generateForm130U,
      filename: 'Form_130U'
    },
    { 
      id: 'billofsale', 
      name: 'Bill of Sale', 
      status: 'pending',
      generator: generateBillOfSale,
      filename: 'Bill_of_Sale'
    },
    { 
      id: 'buyersguide', 
      name: 'Buyers Guide', 
      status: 'pending',
      generator: generateBuyersGuide,
      filename: 'Buyers_Guide'
    },
    { 
      id: 'odometer', 
      name: 'Odometer Disclosure', 
      status: 'pending',
      generator: generateOdometerDisclosure,
      filename: 'Odometer_Disclosure'
    },
    { 
      id: 'insurance', 
      name: 'Insurance Verification', 
      status: 'pending',
      generator: generateInsuranceVerification,
      filename: 'Insurance_Verification'
    },
    { 
      id: 'driverlicense', 
      name: 'Driver License', 
      status: 'pending',
      generator: generateDriverLicenseForm,
      filename: 'Driver_License'
    }
  ]);
  const [generatingForms, setGeneratingForms] = useState(false);

  const formatDealData = () => {
    // Format the deal data to match what the forms expect
    return {
      ...dealData,
      vehicle: dealData?.vehicles_db2025 || {
        year: dealData?.vehicle_year,
        make: dealData?.vehicle_make,
        model: dealData?.vehicle_model,
        vin: dealData?.vehicle_vin,
        mileage: dealData?.vehicle_mileage,
        color: dealData?.vehicle_color,
        body_type: dealData?.vehicle_body_type,
        fuel_type: dealData?.vehicle_fuel_type || 'Gasoline'
      },
      customer: {
        firstName: dealData?.customer_name?.split(' ')[0] || dealData?.customer_first_name || '',
        lastName: dealData?.customer_name?.split(' ').slice(1).join(' ') || dealData?.customer_last_name || '',
        customer_name: dealData?.customer_name || '',
        address: dealData?.customer_address || '',
        city: dealData?.customer_city || '',
        state: dealData?.customer_state || 'TX',
        zipCode: dealData?.customer_zip || '',
        phone: dealData?.customer_phone || '',
        email: dealData?.customer_email || '',
        driversLicense: dealData?.customer_drivers_license || ''
      },
      pricing: {
        vehiclePrice: dealData?.vehicle_price || dealData?.total_price || 0,
        totalAmount: dealData?.total_price || 0,
        downPayment: dealData?.down_payment || 0,
        monthlyPayment: dealData?.monthly_payment || 0,
        tradeInValue: dealData?.trade_value || 0
      },
      warranty: {
        type: dealData?.warranty_type || 'AS_IS'
      },
      trade: {
        allowance: dealData?.trade_value || 0
      }
    };
  };

  const handleDownloadForm = (doc) => {
    try {
      if (doc.generator && typeof doc.generator === 'function') {
        const formattedData = formatDealData();
        const html = doc.generator(formattedData);
        
        if (!html) {
          console.error('Form generator returned empty HTML');
          alert('Error generating form. Please check the deal data.');
          return;
        }
        
        downloadForm(html, `${doc.filename}_${dealData?.id || 'new'}`);
        
        // Update document status
        setDocuments(prev => prev.map(d => 
          d.id === doc.id ? { ...d, status: 'completed' } : d
        ));
      }
    } catch (error) {
      console.error('Error downloading form:', error);
      alert('Error downloading form. Please try again.');
    }
  };

  const handlePrintForm = (doc) => {
    try {
      if (doc.generator && typeof doc.generator === 'function') {
        const formattedData = formatDealData();
        const html = doc.generator(formattedData);
        printForm(html);
      }
    } catch (error) {
      console.error('Error printing form:', error);
      alert('Error printing form. Please try again.');
    }
  };

  const handleDownloadAll = () => {
    documents.forEach(doc => {
      if (doc.generator) {
        setTimeout(() => {
          handleDownloadForm(doc);
        }, 500);
      }
    });
  };

  const handlePrintAll = () => {
    try {
      const formattedData = formatDealData();
      let allHTML = '';
      documents.forEach(doc => {
        if (doc.generator) {
          allHTML += doc.generator(formattedData);
          allHTML += '<div style="page-break-after: always;"></div>';
        }
      });
      printForm(allHTML);
    } catch (error) {
      console.error('Error printing all forms:', error);
      alert('Error printing forms. Please try again.');
    }
  };

  const handleSendDocuments = async () => {
    setGeneratingForms(true);
    try {
      const formattedData = formatDealData();
      // Generate all forms
      const formPromises = documents.map(async (doc) => {
        if (doc.generator) {
          const html = doc.generator(formattedData);
          // Here you would upload to storage and send emails
          // For now, just mark as sent
          return { ...doc, status: 'sent' };
        }
        return doc;
      });

      const updatedDocs = await Promise.all(formPromises);
      setDocuments(updatedDocs);
      
      alert('Documents sent successfully to customer email!');
    } catch (error) {
      console.error('Error sending documents:', error);
      alert('Failed to send documents');
    } finally {
      setGeneratingForms(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg max-w-6xl w-full max-h-[90vh] overflow-hidden">
        {/* Header */}
        <div className="bg-gray-50 px-6 py-4 border-b flex justify-between items-center">
          <div>
            <h2 className="text-2xl font-bold text-gray-900">Digital Deal Jacket</h2>
            <p className="text-sm text-gray-600">
              Deal #{dealData?.id || 'New'} - {dealData?.customer_name || 'Customer'}
            </p>
          </div>
          <button
            onClick={onClose}
            className="text-gray-500 hover:text-gray-700"
          >
            <SafeIcon icon={FiX} className="w-6 h-6" />
          </button>
        </div>

        {/* Tabs */}
        <div className="border-b">
          <div className="flex">
            <button
              onClick={() => setActiveTab('overview')}
              className={`px-6 py-3 font-medium ${
                activeTab === 'overview' 
                  ? 'border-b-2 border-primary-600 text-primary-600' 
                  : 'text-gray-600 hover:text-gray-900'
              }`}
            >
              Overview
            </button>
            <button
              onClick={() => setActiveTab('documents')}
              className={`px-6 py-3 font-medium ${
                activeTab === 'documents' 
                  ? 'border-b-2 border-primary-600 text-primary-600' 
                  : 'text-gray-600 hover:text-gray-900'
              }`}
            >
              Documents
            </button>
            <button
              onClick={() => setActiveTab('upload')}
              className={`px-6 py-3 font-medium ${
                activeTab === 'upload' 
                  ? 'border-b-2 border-primary-600 text-primary-600' 
                  : 'text-gray-600 hover:text-gray-900'
              }`}
            >
              Upload
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="p-6 overflow-y-auto" style={{ maxHeight: 'calc(90vh - 200px)' }}>
          {activeTab === 'overview' && (
            <div>
              <h3 className="text-lg font-semibold mb-4">Deal Summary</h3>
              <div className="grid grid-cols-2 gap-4 mb-6">
                <div className="bg-gray-50 p-4 rounded">
                  <p className="text-sm text-gray-600">Customer</p>
                  <p className="font-medium">{dealData?.customer_name || 'N/A'}</p>
                </div>
                <div className="bg-gray-50 p-4 rounded">
                  <p className="text-sm text-gray-600">Vehicle</p>
                  <p className="font-medium">
                    {dealData?.vehicles_db2025 ? 
                      `${dealData.vehicles_db2025.year} ${dealData.vehicles_db2025.make} ${dealData.vehicles_db2025.model}` :
                      'N/A'
                    }
                  </p>
                </div>
                <div className="bg-gray-50 p-4 rounded">
                  <p className="text-sm text-gray-600">Total Price</p>
                  <p className="font-medium">${dealData?.total_price || 0}</p>
                </div>
                <div className="bg-gray-50 p-4 rounded">
                  <p className="text-sm text-gray-600">Status</p>
                  <p className="font-medium">{dealData?.status || 'Pending'}</p>
                </div>
              </div>
            </div>
          )}

          {activeTab === 'documents' && (
            <div>
              <div className="flex justify-between items-center mb-6">
                <h3 className="text-lg font-semibold">Required Documents</h3>
                <div className="flex gap-2">
                  <button
                    onClick={handleSendDocuments}
                    disabled={generatingForms}
                    className="px-4 py-2 bg-primary-600 text-white rounded hover:bg-primary-700 flex items-center gap-2"
                  >
                    <SafeIcon icon={FiSend} className="w-4 h-4" />
                    Send Documents
                  </button>
                  <button
                    onClick={handlePrintAll}
                    className="px-4 py-2 border border-gray-300 rounded hover:bg-gray-50 flex items-center gap-2"
                  >
                    <SafeIcon icon={FiPrinter} className="w-4 h-4" />
                    Print All
                  </button>
                  <button
                    onClick={handleDownloadAll}
                    className="px-4 py-2 border border-gray-300 rounded hover:bg-gray-50 flex items-center gap-2"
                  >
                    <SafeIcon icon={FiDownload} className="w-4 h-4" />
                    Download All
                  </button>
                </div>
              </div>

              <div className="bg-blue-50 border border-blue-200 rounded p-4 mb-6">
                <p className="text-sm text-blue-800">
                  <strong>Note:</strong> Document generation is being set up. You can use the contract system to generate legal documents.
                </p>
              </div>

              <div className="grid grid-cols-2 gap-4">
                {documents.map(doc => (
                  <div key={doc.id} className="border rounded-lg p-4">
                    <div className="flex items-start justify-between">
                      <div className="flex items-start gap-3">
                        <SafeIcon 
                          icon={FiFileText} 
                          className={`w-10 h-10 p-2 rounded-lg ${
                            doc.status === 'completed' ? 'bg-green-100 text-green-600' :
                            doc.status === 'sent' ? 'bg-blue-100 text-blue-600' :
                            'bg-gray-100 text-gray-600'
                          }`}
                        />
                        <div>
                          <h4 className="font-medium">{doc.name}</h4>
                          <p className="text-sm text-gray-600">
                            Status: <span className={`font-medium ${
                              doc.status === 'completed' ? 'text-green-600' :
                              doc.status === 'sent' ? 'text-blue-600' :
                              'text-yellow-600'
                            }`}>{doc.status}</span>
                          </p>
                        </div>
                      </div>
                      <div className="flex gap-1">
                        <button
                          onClick={() => handleDownloadForm(doc)}
                          className="p-2 text-gray-600 hover:bg-gray-100 rounded"
                          title="Download"
                        >
                          <SafeIcon icon={FiDownload} className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => handlePrintForm(doc)}
                          className="p-2 text-gray-600 hover:bg-gray-100 rounded"
                          title="Print"
                        >
                          <SafeIcon icon={FiPrinter} className="w-4 h-4" />
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {activeTab === 'upload' && (
            <div>
              <h3 className="text-lg font-semibold mb-4">Upload Documents</h3>
              <div className="border-2 border-dashed border-gray-300 rounded-lg p-12 text-center">
                <SafeIcon icon={FiFileText} className="w-12 h-12 mx-auto mb-4 text-gray-400" />
                <p className="text-gray-600 mb-2">Drag and drop files here</p>
                <p className="text-sm text-gray-500 mb-4">or</p>
                <button className="px-4 py-2 bg-primary-600 text-white rounded hover:bg-primary-700">
                  Browse Files
                </button>
                <p className="text-xs text-gray-500 mt-4">Accepted formats: PDF, JPG, PNG (Max 10MB)</p>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default DealJacket;
